import axios from 'axios'
import { publicPath } from '@/../vue.config'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import getAntiguedadPersonal from '@/utils/getAntiguedadPersonal'

export default class CadenaResultados {
  worksheet
  buffer

  constructor(evaluacionDesempenio, personal) {
    this.evaluacionDesempenio = evaluacionDesempenio
    this.personal = personal
  }

  async getFileTemplate() {
    try {
      const file = await axios(`${publicPath}cadena_resultados.xlsx`, { responseType: 'arraybuffer' })
      return file.data
    } catch (error) {
      console.error('Error al obtener la plantilla de la cadena de resultados:', error)
      throw new Error('No se pudo obtener la plantilla de la cadena de resultados.')
    }
  }

  writeCell(cellPosition, value) {
    let cell = null
    cell = this.worksheet.getCell(cellPosition)

    if (value) {
      cell.value = value
    } else {
      cell.value = ''
    }
  }

  writePersonalData() {
    this.writeCell('D9', this.personal.unidadAnalisisTactico) // Número de empleado
    this.writeCell(
      'E9',
      `${this.personal.apaterno} ${this.personal.amaterno} ${this.personal.nombre} ${this.personal.segundoNombre}`
    )
    this.writeCell('F9', this.personal.curp)
    this.writeCell('G9', this.personal.CUIP)
    this.writeCell('H9', this.personal.CatPuestoDependencia ? this.personal.CatPuestoDependencia.catPuesto.puesto : '')

    let antiguedad = getAntiguedadPersonal(this.personal.fechaIngresoEdoFza)
    this.writeCell('I9', antiguedad)
    this.writeCell(
      'J9',
      this.personal.CatPuestoDependencia ? this.personal.CatPuestoDependencia.CatTipoPuesto.title : ''
    )
    this.writeCell(
      'K9',
      this.personal.CatPuestoDependencia ? this.personal.CatPuestoDependencia.catDependencia.nombre : ''
    )
    this.writeCell('L9', this.personal.CatArea ? this.personal.CatArea.nombre : '')
  }

  writeRespetoPrincipios() {
    const evaluacion = this.evaluacionDesempenio.respetoPrincipios

    this.writeCell('N9', evaluacion.fechaAplicacion)
    this.writeCell('O9', evaluacion.puntajeTotal)
    this.writeCell('P9', evaluacion.calificacion)
    this.writeCell('Q9', evaluacion.resultado)
  }

  writeDisciplinaAdministrativa() {
    const evaluacion = this.evaluacionDesempenio.disciplinaAdministrativa
    const faltas = evaluacion.incidencias.find(({ idIncidencia }) => idIncidencia === 1)
    const amonestaciones = evaluacion.incidencias.find(({ idIncidencia }) => idIncidencia === 2)
    const incapacidades = evaluacion.incidencias.find(({ idIncidencia }) => idIncidencia === 3)
    const permisos = evaluacion.incidencias.find(({ idIncidencia }) => idIncidencia === 4)
    const arrestos = evaluacion.incidencias.find(({ idIncidencia }) => idIncidencia === 5)

    this.writeCell('R9', evaluacion.instanciaInformadora)
    this.writeCell('S9', evaluacion.fechaAplicacion)
    this.writeCell('T9', String(faltas.cantidad))
    this.writeCell('U9', String(amonestaciones.cantidad))
    this.writeCell('V9', String(incapacidades.cantidad))
    this.writeCell('W9', String(permisos.cantidad))
    this.writeCell('X9', String(arrestos.cantidad))

    const actas = evaluacion.actasAdministrativas ?? []
    const actasText = actas
      .map(
        (acta, index) =>
          `${index + 1}. Descripción: ${acta.descripcion || ''} Fecha: ${acta.fecha || ''} Motivo: ${acta.motivo || ''}`
      )
      .join('\r\n')
    this.writeCell('Y9', actasText)

    const estimulos = evaluacion.estimulos ?? []
    const estimulosText = estimulos
      .map(
        (estimulo, index) => `${index + 1}. Motivo: ${estimulo.motivo || ''} Descripción: ${estimulo.descripcion || ''}`
      )
      .join('\r\n')
    this.writeCell('Z9', estimulosText)

    const reconocimientos = evaluacion.reconocimientos ?? []
    const reconocimientosText = reconocimientos
      .map(
        (reconocimiento, index) =>
          `${index + 1}. Motivo: ${reconocimiento.motivo || ''} Descripción: ${reconocimiento.descripcion || ''}`
      )
      .join('\r\n')
    this.writeCell('AA9', reconocimientosText)
  }

  writeDisciplinaOperativa() {
    const evaluacion = this.evaluacionDesempenio.disciplinaOperativa

    this.writeCell('AB9', evaluacion.instanciaInformadora)
    this.writeCell('AC9', evaluacion.fechaAplicacion)

    const procedimientosNumeros = (evaluacion.procedimientos ?? [])
      .map((procedimiento, index) => `${index + 1}. ${procedimiento.numero || ''}`)
      .join('\r\n')
    this.writeCell('AH9', procedimientosNumeros)

    const procedimientosFechas = (evaluacion.procedimientos ?? [])
      .map((procedimiento, index) => `${index + 1}. ${procedimiento.fecha || ''}`)
      .join('\r\n')
    this.writeCell('AI9', procedimientosFechas)
  }

  writeProductividad() {
    const evaluacion = this.evaluacionDesempenio.productividad

    this.writeCell('AN9', evaluacion.fechaAplicacion)
    this.writeCell('AO9', evaluacion.puntajeTotal)
    this.writeCell('AP9', evaluacion.calificacion)
    this.writeCell('AQ9', evaluacion.resultado)
  }

  async generateXlsx() {
    const template = await this.getFileTemplate()
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(template)
    const DEFUALT_WORKSHEET = 1
    this.worksheet = workbook.getWorksheet(DEFUALT_WORKSHEET)

    this.writePersonalData()

    if (this.evaluacionDesempenio.respetoPrincipios) {
      this.writeRespetoPrincipios()
    }
    if (this.evaluacionDesempenio.disciplinaAdministrativa) {
      this.writeDisciplinaAdministrativa()
    }
    if (this.evaluacionDesempenio.disciplinaOperativa) {
      this.writeDisciplinaOperativa()
    }
    if (this.evaluacionDesempenio.productividad) {
      this.writeProductividad()
    }

    this.worksheet.removeConditionalFormatting()
    this.buffer = await workbook.xlsx.writeBuffer()

    this.download()
  }

  download() {
    saveAs(
      new Blob([this.buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      'cadena_resultados.xlsx'
    )
  }
}
